import Button from "@/components/button"
import Checkbox from "@/components/checkbox"
import ImagePicker from "@/components/image-picker"
import ListItem from "@/components/list-item"
import Modal from "@/components/modal"
import Space from "@/components/space"
import TextInput from "@/components/text-input"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import { createEmptyRiskFamily, RiskFamily } from "@/entities/risk-family/model/risk-family.types"
import {
  createCodeFromName,
  RISK_FAMILY_TEMPLATES,
  RiskFamilyTemplate
} from "@/entities/risk-family/model/risk-family.utils"
import env from "@/env"
import { remove as removeUpload } from "@/redux/_upload.actions"
import { sortBy } from "lodash"
import { useMemo, useState } from "react"
import RiskFamilyDeleteModal from "./RiskFamilyDeleteModal"

interface Props {
  family?: RiskFamily
  onClose: () => void
  onDelete?: () => void
}

type Mode = "template" | "custom"

export default function RiskFamilyEditModal({ family, onClose, onDelete }: Props) {
  const dispatch = useAppDispatch()
  const status = useAppSelector(riskFamilyStore.selectors.status)
  const existingFamilies = useAppSelector(riskFamilyStore.selectors.list)
  const isNew = !family
  const [draft, setDraft] = useState<RiskFamily>(() => family ?? createEmptyRiskFamily())
  const [mode, setMode] = useState<Mode>(() =>
    !family || RISK_FAMILY_TEMPLATES.some((template) => template.code === family.code) ? "template" : "custom"
  )
  const [modal, setModal] = useState<null | "DELETE">(null)
  const [selectedTemplateCodes, setSelectedTemplateCodes] = useState<string[]>([])
  const templates = useMemo(() => sortBy(RISK_FAMILY_TEMPLATES, (template) => normalizeText(template.name)), [])
  const existingCodes = new Set(existingFamilies.filter((item) => item.id !== draft.id).map((item) => item.code))
  const isCustom = mode === "custom"
  const showTemplateChecklist = isNew && mode === "template"
  const showForm = !isNew || isCustom
  const canSave = showTemplateChecklist ? selectedTemplateCodes.length > 0 : draft.name.trim().length > 0

  function normalizeText(value: string) {
    return value
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
  }

  function edit<K extends keyof RiskFamily>(key: K, value: RiskFamily[K]) {
    setDraft((item) => ({ ...item, [key]: value }))
  }

  function handleSelectTemplate(template: RiskFamilyTemplate) {
    if (existingCodes.has(template.code)) return

    setSelectedTemplateCodes((codes) =>
      codes.includes(template.code) ? codes.filter((code) => code !== template.code) : codes.concat(template.code)
    )
  }

  function handleCustomMode() {
    setMode("custom")
    setDraft(createEmptyRiskFamily())
  }

  async function handleRemoveImage() {
    if (draft.isImageCustom) {
      await dispatch(removeUpload("RiskFamilies", draft.id, "png") as any)
    }

    edit("isImageCustom", false)
  }

  async function handleSave() {
    if (!canSave) return

    if (isNew && showTemplateChecklist) {
      for (const templateCode of selectedTemplateCodes) {
        const template = templates.find((item) => item.code === templateCode)
        if (template) {
          await dispatch(
            riskFamilyStore.thunks.createEntity(
              createEmptyRiskFamily({
                code: template.code,
                name: template.name
              })
            )
          ).unwrap()
        }
      }
    } else if (isNew) {
      const familyToSave = {
        ...draft,
        code: createCodeFromName(draft.name, existingCodes)
      }
      await dispatch(riskFamilyStore.thunks.createEntity(familyToSave)).unwrap()
    } else {
      const familyToSave = draft
      dispatch(riskFamilyStore.actions.setActive(familyToSave))
      const response = await dispatch(riskFamilyStore.thunks.updateEntity()).unwrap()
      if (response) {
        dispatch(riskFamilyStore.actions.replace(response))
      }
    }

    onClose()
  }

  return (
    <Modal
      isLarge
      status={status}
      isCloseButtonVisible
      title={isNew ? "Ajouter famille de risque" : `Modifier ${family.name}`}
      onClose={onClose}
      onDelete={!isNew ? () => setModal("DELETE") : undefined}
      onNext={handleSave}
      onNextCta="Enregistrer"
      nextDisabled={!canSave}
      disableClickOutside
      footer={
        showTemplateChecklist ? (
          <Button isWithBorder onClick={handleCustomMode}>
            + Nouvelle famille
          </Button>
        ) : undefined
      }>
      {modal === "DELETE" && family && (
        <RiskFamilyDeleteModal
          family={family}
          onClose={() => setModal(null)}
          onDelete={() => {
            setModal(null)
            onDelete?.()
            onClose()
          }}
        />
      )}

      {showTemplateChecklist && (
        <div className="modal-content-list max-h-[460px] overflow-auto">
          {templates.map((template) => {
            const isSelected = selectedTemplateCodes.includes(template.code)
            const isDisabled = existingCodes.has(template.code)

            return (
              <ListItem
                key={template.code}
                isEditable={!isDisabled}
                isActive={isSelected}
                className={isDisabled ? "opacity-50" : ""}
                onClick={() => handleSelectTemplate(template)}>
                <Checkbox active={isSelected} onClick={() => handleSelectTemplate(template)} disabled={isDisabled} />
                <img
                  alt={template.code}
                  className="mx-10 h-[34px] w-[34px] object-contain"
                  src={env.REACT_APP_URL_SPACE.concat("/RiskFamilies/", template.code, ".png")}
                />
                <div className="min-w-0">
                  <div>{template.name}</div>
                </div>
                <Space />
                {isDisabled && <div className="grey-t text-sm">Déjà ajoutée</div>}
              </ListItem>
            )
          })}
        </div>
      )}

      {showForm && (
        <div className="grid grid-cols-[minmax(0,1fr)_220px] items-start gap-40">
          <div>
            <TextInput
              title="Nom"
              value={draft.name}
              onChange={(e) => edit("name", e.value)}
              required
              autoFocus={isCustom}
            />
          </div>

          <div className="flex justify-center pt-2">
            <ImagePicker
              id={draft.id}
              imageId={draft.isImageCustom ? draft.id : null}
              model="RiskFamilies"
              onDelete={handleRemoveImage}
              onSave={() => edit("isImageCustom", true)}
              square
              text="Charger une image"
            />
          </div>
        </div>
      )}
    </Modal>
  )
}
